import { Injectable } from '@angular/core';
import { _getFwUpgradeConfig as getProductionConfig, FirmwareConfig, InverterUpgradeConfig, ChipData } from 'ngx-modbus';
import { Observable } from 'rxjs';

import { TestConfigurationService, SafetyOverrides } from '../test-configuration/test-configuration.service';

/**
 * Service that provides firmware configuration with test mode support.
 *
 * This wraps the base ngx-modbus getFwUpgradeConfig and adds test mode interception.
 * When in test mode, it merges production config with user-specific test overrides.
 */
@Injectable({ providedIn: 'root' })
export class FirmwareConfigService {

  constructor(private testConfigService: TestConfigurationService) {}

  /**
   * Enable or disable test mode (Firestore-based)
   */
  async setTestMode(enabled: boolean): Promise<void> {
    await this.testConfigService.setTestMode(enabled);
  }

  /**
   * Get current test mode status (async)
   */
  async isTestModeEnabled(): Promise<boolean> {
    return await this.testConfigService.getTestMode();
  }

  /**
   * Observe test mode state changes
   */
  observeTestMode(): Observable<boolean> {
    return this.testConfigService.observeTestMode();
  }

  /**
   * Get firmware configuration (production or test mode)
   *
   * This is the main entry point for getting firmware config.
   * It automatically applies test overrides when in test mode.
   */
  async getFirmwareConfig(): Promise<FirmwareConfig> {
    // Get production config from ngx-modbus
    const productionConfig = await getProductionConfig();

    // If not in test mode, return production config as-is
    const isTestMode = await this.isTestModeEnabled();
    if (!isTestMode) {
      return productionConfig;
    }

    // Test mode: apply overrides
    try {
      // Validate overrides (cleanup invalid paths automatically)
      const validation = await this.testConfigService.validateOverrides();

      // Merge production config with INVERTER/LOGGER test overrides
      let mergedConfig = this.testConfigService.mergeConfigWithOverrides(
        productionConfig,
        validation.cleanedOverrides
      );

      // Apply SAFETY overrides
      const safetyOverrides = await this.testConfigService.getSafetyOverrides();
      mergedConfig = this.applySafetyOverrides(mergedConfig, safetyOverrides);

      return mergedConfig;
    } catch (error) {
      console.error('[FirmwareConfig] Failed to apply test overrides, falling back to production', error);
      return productionConfig;
    }
  }

  /**
   * Apply SAFETY test overrides to firmware config.
   *
   * For each inverter pattern in config, check if there's a SAFETY override
   * for the device model (extracted from pattern, e.g., "ZM2_*" → "ZM2").
   *
   * - binaryLink: REPLACES the first SAFETY entry's LINK
   * - customLinks: APPENDS to SAFETY_CUSTOM array
   */
  private applySafetyOverrides(config: FirmwareConfig, safetyOverrides: SafetyOverrides): FirmwareConfig {
    if (!safetyOverrides || Object.keys(safetyOverrides).length === 0) {
      return config;
    }

    for (const [pattern, deviceConfig] of Object.entries(config)) {
      // Only process inverter configs
      if ((deviceConfig as InverterUpgradeConfig).DEVICE_MODEL_ID !== 'INVERTER') {
        continue;
      }

      const inverterConfig = deviceConfig as InverterUpgradeConfig;

      // Extract deviceModel from pattern (e.g., "ZM2_*" → "ZM2", "ZM2-XY_*" → "ZM2-XY")
      const deviceModel = pattern.split('_')[0];

      const override = safetyOverrides[deviceModel];
      if (!override) {
        continue;
      }

      // Replace SAFETY binary link
      if (override.binaryLink) {
        if (inverterConfig.SAFETY?.length) {
          // Replace the link in the first SAFETY entry
          inverterConfig.SAFETY[0].LINK = override.binaryLink;
        } else {
          // Create SAFETY entry if doesn't exist
          inverterConfig.SAFETY = [{
            CHIP: 'TEST_SAFETY',
            LINK: override.binaryLink,
            LATEST_VERSION: '',
            MIN_VERSION: '',
            MODEL: deviceModel
          }];
        }
      }

      // Append custom links to SAFETY_CUSTOM
      if (override.customLinks?.length) {
        const existingCustom: ChipData[] = inverterConfig.SAFETY_CUSTOM || [];

        const testCustomEntries: ChipData[] = override.customLinks.map((link, index) => ({
          CHIP: `TEST_CUSTOM_${index}`,
          LINK: link,
          LATEST_VERSION: '',
          MIN_VERSION: '',
          MODEL: deviceModel
        }));

        inverterConfig.SAFETY_CUSTOM = [...existingCustom, ...testCustomEntries];
      }
    }

    return config;
  }
}
